import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	ActionIcon,
	Box,
	Burger,
	Button,
	createStyles,
	Drawer,
	Flex,
	Group,
	Modal,
	Overlay,
	Text,
	Tooltip,
	useMantineTheme,
} from '@mantine/core';
import { IconBrandGraphql, IconTank } from '@tabler/icons-react';
import * as Cesium from 'cesium';
import { ImageryLayer, Viewer } from 'resium';
import Preloader from '../../../../components/common/Preloader';
import { AppInfoContext } from '../../../../components/contexts/AppInfoContext';
import { AssessmentScenarioData } from '../../../../types/assessments';
import { BehaviorParameter, BehaviorParameterTemplate } from '../../../../types/behaviors';
import { ListForcePackageData, Optional } from '../../../../types/common';
import { Echelon, ParentEchelon } from '../../../../types/force-packages';
import { InputData } from '../../../../types/systems';
import { downloadForcePackageSettings } from '../../../ForcePackages/downloadForcePackageSettings';
import BehaviorEditiorContextProvider from './contexts/BehaviorEditiorContext';
import BehaviorNestedAccordions from './force-package-accordions/BehaviorNestedAccordions';
import BehaviorPanel from './force-package-accordions/BehaviorPanel';
import {
	getBehaviorName,
	getBehaviorTemplate,
	getCurrentQueueProperty,
	getDependsOnProperty,
	getNextQueueEntry,
	getPlanningAction,
	getQueueEntry,
} from './functions/behaviorSettingsFunctions';
import BaseLayer from './layers/BaseLayer';
import { EchelonsLayer } from './layers/EchelonsLayer';
import PathLayer from './layers/PathLayer';
import RadiusLayer from './layers/RadiusLayer';
import SystemsLayer from './layers/SystemsLayer';
import VoronoisLayer from './layers/VoronoisLayer';

interface IBehaviorsEditor {
	title: string;
	buttonText: string;
	scenarioSettings: Optional<AssessmentScenarioData, 'id'>;
	disabled: boolean;
	disabledReason?: string;
	defaultValue: string | string[] | number | number[];
	voronoisDataSource: Cesium.GeoJsonDataSource | null | undefined;
	elementOnWhichInputDepends?: BehaviorParameter;
	behaviorSettingsProperty: 'blue_behavior_settings' | 'red_behavior_settings';
	echelonSettingsProperty: 'blue_echelon_settings' | 'red_echelon_settings';
	inputData: InputData;
	onChange: (value: any) => void;
	setSetting: (
		settingName: string,
		value: AssessmentScenarioData[keyof AssessmentScenarioData]
	) => void;
}

const BehaviorsEditor: React.FC<IBehaviorsEditor> = ({
	title,
	buttonText,
	defaultValue,
	scenarioSettings,
	disabled,
	disabledReason,
	voronoisDataSource,
	elementOnWhichInputDepends,
	behaviorSettingsProperty,
	echelonSettingsProperty,
	inputData,
	onChange,
	setSetting,
}) => {
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const { envVariables } = useContext(AppInfoContext);

	const [forcePackage, setForcePackage] = useState<null | ParentEchelon>(null);
	const [systemsIds, setSystemsIds] =
		useState<{ agent_id: number; system_id: number; is_commander: boolean }[]>();
	const [echelonsIds, setEchelonsIds] =
		useState<{ agent_id: number; system_id: number; is_commander: boolean }[]>();
	const [isOpened, setIsOpened] = useState(false);
	const [viewer, setViewer] = useState<Cesium.Viewer | null>(null);
	const [isDataLoading, setIsDataLoading] = useState(false);
	const [isBaseLayerLoaded, setIsBaseLayerLoaded] = useState(false);
	const [symbolsCreated, setSymbolsCreated] = useState(false);
	const [currentArea, setCurrentArea] = useState<
		(Cesium.Entity & { properties: Record<string, any> }) | null
	>(null);

	const [openedAccordionsNames, setOpenedAccordionsNames] = useState<string[]>([]);
	const [accordionsOpened, setAccordionsOpened] = useState(false);
	const [currentSymbolId, setCurrentSymbolId] = useState<number | null>(null);
	const [currentEchelonId, setCurrentEchelonId] = useState<number | null>(null);
	const [visibleEchelons, setVisibleEchelons] = useState<number[]>([]); // to keep track of visible echelons in the map

	const [layerControls, setLayerControls] = useState<JSX.Element | null>(null); // controls when in edition mode
	const [showSystemsLayer, setShowSystemsLayer] = useState(true);
	const [showEchelonsLayer, setShowEchelonsLayer] = useState(false);

	const topLevelEchelonSettings = useMemo<BehaviorParameterTemplate[]>(() => {
		return [
			{ property: 'ACTIVATION_DELAY', type: 'double', default: 0 },
			{ property: 'DEPLOYMENT_ZONE', type: 'deploymentZone', default: -1 },
		];
	}, []);

	const updateIsOpened = useCallback(
		(value: boolean) => {
			if (!value) {
				setIsBaseLayerLoaded(false);
				viewer?.dataSources.removeAll();
				setViewer(null);
				setCurrentSymbolId(null);
				setCurrentEchelonId(null);
				setCurrentArea(null);
				setSymbolsCreated(false);
			}
			setIsOpened(value);
		},
		[viewer]
	);

	const setBehaviorsSetting = (
		value: Record<number, BehaviorParameter[]> | null,
		propertyName: string
	) => {
		if (value) setSetting(propertyName, value);
	};

	const getLayer = useCallback(
		(
			key: string,
			defaultValue: number,
			systemSettings: ListForcePackageData & {
				cost: string | number;
				symbol: string;
			},
			system: { agent_id: number; system_id: number; is_commander: boolean },
			isPreview: boolean,
			isEchelonLayer: boolean,
			dependsOn?: BehaviorParameter,
			queueInputIndex?: number
		): Record<string, JSX.Element | null> => {
			return {
				mapNode: viewer ? (
					<VoronoisLayer
						key={key}
						viewer={viewer}
						defaultValue={defaultValue || -1}
						systemSettings={systemSettings}
						systemIds={system}
						onChange={() => {}}
						previewMode={isPreview}
						isEditionModeOn={false}
						queueInputIndex={queueInputIndex}
						elementOnWhichInputDepends={dependsOn}
						isEchelonSettings={isEchelonLayer}
					/>
				) : null,
				mapRadius: viewer ? (
					<RadiusLayer
						key={key}
						viewer={viewer}
						defaultValue={defaultValue | 0}
						systemSettings={systemSettings}
						voronoisDataSource={voronoisDataSource}
						onChange={() => {}}
						systemIds={system}
						previewMode={isPreview}
						isEditionModeOn={false}
						queueInputIndex={queueInputIndex}
						elementOnWhichInputDepends={dependsOn}
						isEchelonSettings={isEchelonLayer}
					/>
				) : null,
				mapPath: viewer ? (
					<PathLayer
						key={key}
						viewer={viewer}
						defaultValue={defaultValue || []}
						systemIds={system}
						onChange={() => {}}
						previewMode={isPreview}
						isEditionModeOn={false}
						queueInputIndex={queueInputIndex}
						elementOnWhichInputDepends={dependsOn}
						isEchelonSettings={isEchelonLayer}
					/>
				) : null,
			};
		},
		[viewer, voronoisDataSource]
	);

	const updateCurrentArea = useCallback(() => {
		if (viewer && isBaseLayerLoaded) {
			const areas = viewer.dataSources?.getByName('areas')[0]?.entities
				?.values as (Cesium.Entity & {
				properties: Record<string, any>;
			})[];

			if (areas) {
				const currentArea =
					areas.find((x) => x.properties.type._value.toLowerCase().includes('staging')) ||
					null;
				setCurrentArea(currentArea);
			}
		}
	}, [viewer, isBaseLayerLoaded]);

	const getPreviewLayers = useCallback(
		(
			systemIx: string,
			system: { agent_id: number; system_id: number; is_commander: boolean },
			template: BehaviorParameterTemplate[],
			planningActions: BehaviorParameter,
			isEchelonPreview: boolean = false,
			queueInputIndex?: number,
			elementOnWhichInputDepends?: BehaviorParameter // if called by getQueueLayersRecursively
		) => {
			const getQueueLayersRecursively = (
				entry: BehaviorParameter | undefined,
				depth: number,
				elementOnWhichInputDepends?: BehaviorParameter
			): (JSX.Element | null)[] => {
				if (entry) {
					const planningActions = getPlanningAction(entry?.settings);
					const planningActionName = getBehaviorName(planningActions);
					const template = getBehaviorTemplate(
						inputData,
						planningActionName,
						'PLANNING_ACTION'
					);

					if (template) {
						const currentLayers =
							planningActions && template
								? getPreviewLayers(
										`${systemIx}${depth}`,
										system,
										template,
										planningActions,
										isEchelonPreview,
										depth + 1,
										elementOnWhichInputDepends
									)
								: [];

						const layers: (JSX.Element | null)[] = [
							...currentLayers,
							...getQueueLayersRecursively(
								getNextQueueEntry(entry),
								depth + 1,
								getCurrentQueueProperty(entry)
							),
						];
						return layers;
					}
				}
				return [];
			};

			const systemSettings = scenarioSettings.systems[system.system_id];

			const layers = (planningActions.settings as BehaviorParameter[]).map(
				(setting, settingIx) => {
					const inputTemplate = template.find(
						(template) => template.property === setting.property
					) as BehaviorParameterTemplate;
					const dependsOn =
						getDependsOnProperty(planningActions, inputTemplate?.dependsOn) ||
						elementOnWhichInputDepends;

					if (inputTemplate) {
						if (inputTemplate.type === 'actionQueue') {
							return getQueueLayersRecursively(getQueueEntry(setting), 0);
						} else {
							return getLayer(
								systemIx + inputTemplate.property + settingIx,
								setting.value as number,
								systemSettings,
								system,
								true,
								isEchelonPreview,
								dependsOn,
								queueInputIndex
							)[inputTemplate.type];
						}
					} else {
						return null;
					}
				}
			);

			return layers.flat();
		},
		[scenarioSettings, getLayer, inputData]
	);

	const getChildrenEchelonsIds = (echelons: Echelon[]) => {
		let ids: number[] = [];
		echelons.forEach((echelon) => {
			ids.push(echelon.id);
			if (echelon.echelons && echelon.echelons.length > 0) {
				ids = ids.concat(getChildrenEchelonsIds(echelon.echelons));
			}
		});
		return ids;
	};

	useEffect(() => {
		updateCurrentArea();
	}, [updateCurrentArea]);

	// get list of all systems ids
	useEffect(() => {
		const getSystemsIds = (
			parentEchelon: Echelon
		): { agent_id: number; system_id: number; is_commander: boolean }[] => {
			const systems: { agent_id: number; system_id: number; is_commander: boolean }[] = [];

			if (parentEchelon.systems) {
				parentEchelon.systems.forEach((system) => {
					systems.push({ ...system, is_commander: parentEchelon.type !== 1 });
				});
			}

			if (parentEchelon.echelons) {
				parentEchelon.echelons.forEach((echelon) => {
					systems.push(...getSystemsIds(echelon));
				});
			}

			return systems;
		};

		if (!systemsIds && forcePackage) {
			const systems = getSystemsIds(forcePackage.echelons[0]);
			setSystemsIds(systems);
		}
	}, [systemsIds, forcePackage]);

	// get list of all echelons ids
	useEffect(() => {
		const getEchelonsIds = (fp: ParentEchelon) => {
			const echelons: number[] = [];

			fp.echelons.forEach((echelon) => {
				echelons.push(echelon.id);
				if (echelon.echelons) {
					echelons.push(...getEchelonsIds(echelon));
				}
			});

			return echelons;
		};

		if (!echelonsIds && forcePackage) {
			const echelons = getEchelonsIds(forcePackage).map((id) => ({
				agent_id: id,
				system_id: id,
				is_commander: false,
			}));
			setEchelonsIds(echelons);
		}
	}, [echelonsIds, forcePackage]);

	// Download force package settings and update visible echelons
	useEffect(() => {
		const downloadFp = async () => {
			const forcePackage = await downloadForcePackageSettings(
				scenarioSettings[
					behaviorSettingsProperty === 'blue_behavior_settings'
						? 'blue_force_package'
						: 'red_force_package'
				] as number
			);

			if (forcePackage) {
				setForcePackage(forcePackage.fp as ParentEchelon);
				setVisibleEchelons(getChildrenEchelonsIds(forcePackage.fp.echelons));
			}
		};

		if (!forcePackage) downloadFp();
	}, [scenarioSettings, behaviorSettingsProperty, forcePackage]);

	// Set events for system selection
	useEffect(() => {
		const setSelectedSymbolId = (selectedEntity: Cesium.Entity) => {
			if (selectedEntity && viewer) {
				const entity = viewer.entities.values.find((x) => x.id === selectedEntity.id);
				if (entity) {
					if (selectedEntity.id.includes('echelon')) {
						setCurrentEchelonId(parseInt(entity.id.split('echelon')[1]));
						setCurrentSymbolId(null);
					} else {
						setCurrentSymbolId(parseInt(entity.id.split('symbol')[1]));
						setCurrentEchelonId(null);
					}
				}
			} else {
				setCurrentSymbolId(null);
				setCurrentEchelonId(null);
			}
		};

		viewer && viewer.selectedEntityChanged.addEventListener(setSelectedSymbolId);

		return () => {
			viewer && viewer.selectedEntityChanged.removeEventListener(setSelectedSymbolId);
		};
	}, [viewer]);

	// Select symbol on system click in fp
	useEffect(() => {
		if (viewer && currentSymbolId !== null) {
			const entity = viewer.entities.values.find((x) => x.id === 'symbol' + currentSymbolId);
			viewer.selectedEntity = entity;
			setAccordionsOpened(false);
		}

		if (currentSymbolId === null) {
			setLayerControls(null);
		}
	}, [currentSymbolId, viewer]);

	// Select echelon on system click in fp
	useEffect(() => {
		if (viewer && currentEchelonId !== null) {
			const entity = viewer.entities.values.find(
				(x) => x.id === 'echelon' + currentEchelonId
			);
			viewer.selectedEntity = entity;
			setAccordionsOpened(false);
		}

		if (currentEchelonId === null) {
			setLayerControls(null);
		}
	}, [currentEchelonId, viewer]);

	return (
		<BehaviorEditiorContextProvider viewer={viewer}>
			{isOpened &&
			systemsIds &&
			systemsIds.length > 0 &&
			echelonsIds &&
			echelonsIds.length > 0 ? (
				<Modal
					className={classes.modal}
					opened={isOpened}
					onClose={() => updateIsOpened(false)}
					title={title}
					size={'90vw'}
					fullScreen
				>
					<Flex h={'80vh'}>
						<Viewer
							full
							ref={(e) => {
								if (e && e.cesiumElement) {
									setViewer(e.cesiumElement);
								}
							}}
							animation={false}
							timeline={false}
							baseLayerPicker={false}
							navigationHelpButton={false}
							sceneModePicker={false}
							homeButton={false}
							geocoder={false}
							fullscreenButton={false}
						>
							<ImageryLayer
								imageryProvider={
									new Cesium.UrlTemplateImageryProvider({
										url: `${envVariables?.IMAGERY_URL}/styles/maplibre-night-topo/{z}/{x}/{y}.png?access_token=${envVariables?.MAP_KEY}`,
										minimumLevel: 0,
										maximumLevel: 20,
										credit: new Cesium.Credit(
											'\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e',
											true
										),
									})
								}
							/>

							<BaseLayer
								viewer={viewer as Cesium.Viewer}
								side={behaviorSettingsProperty === 'blue_behavior_settings' ? 0 : 1}
								areasData={inputData.areasData}
								voronoisDataSource={voronoisDataSource}
								elementOnWhichInputDepends={elementOnWhichInputDepends}
								setIsDataLoading={setIsDataLoading}
								setIsBaseLayerLoaded={setIsBaseLayerLoaded}
							/>

							{viewer && isBaseLayerLoaded ? (
								<SystemsLayer
									viewer={viewer as Cesium.Viewer}
									side={
										behaviorSettingsProperty === 'blue_behavior_settings'
											? 0
											: 1
									}
									systemIds={systemsIds}
									scenarioSettings={scenarioSettings}
									currentArea={currentArea}
									behaviorSettingsProperty={behaviorSettingsProperty}
									visible={showSystemsLayer}
									onSystemsCreated={() => setSymbolsCreated(true)}
								></SystemsLayer>
							) : null}

							{showSystemsLayer &&
							isBaseLayerLoaded &&
							currentArea &&
							!layerControls &&
							currentSymbolId === null &&
							currentEchelonId === null &&
							symbolsCreated
								? systemsIds.map((system, ix) => {
										const planningActions = scenarioSettings[
											behaviorSettingsProperty
										][system.agent_id]?.find(
											(behavior) => behavior.property === 'PLANNING_ACTION'
										);
										const planningActionName = getBehaviorName(planningActions);
										const template = getBehaviorTemplate(
											inputData,
											planningActionName,
											'PLANNING_ACTION'
										);

										if (
											template &&
											planningActions &&
											planningActions.settings &&
											viewer
										) {
											return getPreviewLayers(
												'symbol' + ix.toString(),
												system,
												template,
												planningActions,
												false
											);
										}
										return null;
									})
								: null}

							{forcePackage && viewer && isBaseLayerLoaded && symbolsCreated ? (
								<EchelonsLayer
									viewer={viewer as Cesium.Viewer}
									forcePackage={forcePackage}
									side={
										behaviorSettingsProperty === 'blue_behavior_settings'
											? 0
											: 1
									}
									visible={showEchelonsLayer}
									systemsVisible={showSystemsLayer}
									visibleEchelons={visibleEchelons}
								></EchelonsLayer>
							) : null}

							{showEchelonsLayer &&
							isBaseLayerLoaded &&
							currentArea &&
							!layerControls &&
							currentEchelonId === null &&
							currentSymbolId === null
								? echelonsIds.map((echelon, ix) => {
										const planningActions = scenarioSettings[
											echelonSettingsProperty
										][echelon.agent_id]?.find(
											(behavior) =>
												behavior.property === 'ECHELON_PLANNING_ACTION'
										);
										const planningActionName = getBehaviorName(planningActions);
										const template = getBehaviorTemplate(
											inputData,
											planningActionName,
											'ECHELON_PLANNING_ACTION'
										);

										if (
											template &&
											planningActions &&
											planningActions.settings &&
											viewer
										) {
											return getPreviewLayers(
												'echelon' + ix.toString(),
												echelon,
												template,
												planningActions,
												true
											);
										}
										return null;
									})
								: null}

							{/* System and echelons behaviors panel */}
							{(currentSymbolId !== null || currentEchelonId !== null) && viewer ? (
								<Box
									style={{
										display: 'flex',
										position: 'absolute',
										height: 'fit-content',
										maxHeight: 'calc(100% - 3rem - 20px)',
										overflowY: 'auto',
										padding: '0 1rem 1rem 1rem',
										top: 'calc(3rem + 10px)',
										left: 50,
										borderRadius: '0.25rem',
										backgroundColor:
											theme.colorScheme === 'dark'
												? theme.colors.dark[0]
												: theme.colors.light[0],
									}}
								>
									{currentSymbolId !== null &&
									systemsIds.find(
										(system) => system.agent_id === currentSymbolId
									) ? (
										<Flex style={{ position: 'relative' }}>
											<BehaviorPanel
												key={currentSymbolId}
												cesiumViewer={viewer as Cesium.Viewer}
												entityId={currentSymbolId}
												behaviorSettings={
													scenarioSettings[behaviorSettingsProperty]
												}
												inputData={inputData}
												scenarioSettings={scenarioSettings}
												setSetting={(value) =>
													setBehaviorsSetting(
														value,
														behaviorSettingsProperty
													)
												}
												systemId={
													systemsIds.find(
														(system) =>
															system.agent_id === currentSymbolId
													)?.system_id as number
												}
												isCommander={
													systemsIds.find(
														(system) =>
															system.agent_id === currentSymbolId
													)?.is_commander as boolean
												}
												forcePackage={forcePackage}
												isEditionModeOn={layerControls !== null}
												setLayerControls={(value: JSX.Element | null) =>
													setLayerControls(value)
												}
												isEchelonLevel={false}
											></BehaviorPanel>
										</Flex>
									) : currentEchelonId !== null ? (
										<Flex style={{ position: 'relative' }}>
											<BehaviorPanel
												key={currentEchelonId}
												cesiumViewer={viewer as Cesium.Viewer}
												entityId={currentEchelonId}
												behaviorSettings={
													scenarioSettings[echelonSettingsProperty]
												}
												inputData={inputData}
												scenarioSettings={scenarioSettings}
												setSetting={(value) =>
													setBehaviorsSetting(
														value,
														echelonSettingsProperty
													)
												}
												systemId={currentEchelonId}
												isCommander={false}
												forcePackage={forcePackage}
												isEditionModeOn={layerControls !== null}
												setLayerControls={(value: JSX.Element | null) =>
													setLayerControls(value)
												}
												topLevelSettings={topLevelEchelonSettings}
												isEchelonLevel
											></BehaviorPanel>
										</Flex>
									) : null}
								</Box>
							) : null}

							<Drawer
								className={classes.drawer}
								opened={accordionsOpened}
								onClose={() => setAccordionsOpened(false)}
								w={'40%'}
							>
								<Box
									style={{
										width: '100%',
										position: 'relative',
										padding: '1rem',
										backgroundColor:
											theme.colorScheme === 'dark'
												? theme.colors.dark[0]
												: theme.colors.light[0],
									}}
								>
									<Flex
										mb={'2rem'}
										gap={'1rem'}
										p={'1rem'}
										style={{
											justifyContent: 'space-evenly',
											border: `2px solid ${
												theme.colorScheme === 'dark'
													? theme.colors.dark[1]
													: theme.colors.light[2]
											}`,
										}}
									>
										<Flex style={{ alignItems: 'center' }}>
											<ActionIcon
												variant="filled"
												title="Toggle echelons visibility"
												color={showEchelonsLayer ? 'green.5' : 'gray.5'}
												bg={showEchelonsLayer ? 'green' : 'gray'}
												w={'2.25rem'}
												h={'2.25rem'}
												mr={'0.5rem'}
												onClick={() =>
													setShowEchelonsLayer((prev) => !prev)
												}
											>
												<IconBrandGraphql size="1.125rem" />
											</ActionIcon>
											<Text
												color={
													theme.colorScheme === 'dark'
														? theme.colors.dark[2]
														: theme.colors.light[2]
												}
											>
												Show/Hide Echelons
											</Text>
										</Flex>
										<Flex style={{ alignItems: 'center' }}>
											<ActionIcon
												variant="filled"
												title="Toggle systems visibility"
												color={showSystemsLayer ? 'green.5' : 'gray.5'}
												bg={showSystemsLayer ? 'green' : 'gray'}
												w={'2.25rem'}
												h={'2.25rem'}
												mr={'0.5rem'}
												onClick={() => setShowSystemsLayer((prev) => !prev)}
											>
												<IconTank size="1.125rem" />
											</ActionIcon>
											<Text
												color={
													theme.colorScheme === 'dark'
														? theme.colors.dark[2]
														: theme.colors.light[2]
												}
											>
												Show/Hide Systems
											</Text>
										</Flex>
									</Flex>

									{forcePackage ? (
										<BehaviorNestedAccordions
											forcePackage={forcePackage}
											scenarioSettings={scenarioSettings}
											pathToElement={[]}
											inputData={inputData}
											behaviorSettingsProperty={behaviorSettingsProperty}
											echelonSettingsProperty={echelonSettingsProperty}
											openedAccordionsNames={openedAccordionsNames}
											visibleEchelons={visibleEchelons}
											isTopLevel={true}
											topLevelSettings={topLevelEchelonSettings}
											setSetting={setSetting}
											setOpenedAccordionsNames={(value: string[]) =>
												setOpenedAccordionsNames(value)
											}
											updateSelectedSymbolId={(id: number | null) =>
												showSystemsLayer && setCurrentSymbolId(id)
											}
											updateSelectedEchelonId={(id: number | null) =>
												showEchelonsLayer && setCurrentEchelonId(id)
											}
											setVisibleEchelons={(
												echelon: Echelon,
												checked: boolean
											) => {
												const ids = getChildrenEchelonsIds([echelon]);
												setVisibleEchelons((prev) => {
													if (checked) {
														return Array.from(
															new Set([...prev, ...ids])
														);
													}
													return prev.filter((id) => !ids.includes(id));
												});
											}}
										></BehaviorNestedAccordions>
									) : (
										<Preloader />
									)}
								</Box>
							</Drawer>

							<Flex
								style={{
									position: 'absolute',
									top: 'calc(3rem)',
									left: 0,
									flexDirection: 'row',
									background: '#1a1b1e',
								}}
							>
								<Burger
									onClick={() => setAccordionsOpened(true)}
									opened={accordionsOpened}
								/>
							</Flex>

							{isDataLoading || !isBaseLayerLoaded ? (
								<Overlay style={{ display: 'flex' }}>
									<Preloader />
								</Overlay>
							) : null}

							{layerControls}
						</Viewer>
					</Flex>
				</Modal>
			) : null}

			<Group position="center">
				<Tooltip
					label={disabledReason}
					disabled={!disabled || !disabledReason}
					withinPortal
					multiline
					width={260}
				>
					<Box w={'100%'}>
						<Button
							w={'100%'}
							onClick={() => updateIsOpened(true)}
							disabled={disabled}
							component="div"
							variant={'filled'}
						>
							{/* component="div" is needed to prevent html from throwing wrong nesting error if used inside Accordion.Control */}
							{buttonText}
						</Button>
					</Box>
				</Tooltip>
			</Group>
		</BehaviorEditiorContextProvider>
	);
};

const useStyles = createStyles((theme, _params, _variations) => ({
	modal: {
		'.mantine-Modal-header': {
			height: '3rem',
		},
	},
	drawer: {
		'.mantine-Drawer-content': {
			flex: '0 1 auto',
			width: '40vw',
			backgroundColor:
				theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.light[0],
		},
		'.mantine-Drawer-header': {
			height: '3rem',
			backgroundColor:
				theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.light[0],
		},
		'.mantine-Drawer-body': {
			padding: 0,
		},
	},
}));

export default BehaviorsEditor;
